import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen } from 'lucide-react';
import { PublicCatalogueLink } from './PublicCatalogueLink';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true;

// Livraison du PDF : partage natif (mode application / mobile) sinon téléchargement classique
const deliverPdf = async (blob, filename) => {
  const file = new File([blob], filename, { type: 'application/pdf' });
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: 'Catalogue Ô\'Delicieuse' });
      return 'shared';
    } catch (e) {
      if (e?.name === 'AbortError') return 'cancelled';
    }
  }
  if (isStandalone()) {
    window.open(URL.createObjectURL(blob), '_blank');
    return 'opened';
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  return 'downloaded';
};

/**
 * Admin catalogue PDF download modal.
 *
 * Extracted from AdminDashboard to keep the dashboard small and the modal
 * independently testable.
 *
 * Behaviour (unchanged):
 * - Scope: "all" or "recent" + period in days (7/14/30/60)
 * - Format: fast text-only (axios download) OR images (open in new tab via
 *   short-lived signed token — required by iOS Safari)
 */
export const CataloguePdfModal = ({ open, onClose }) => {
  const [filter, setFilter] = useState('all');
  const [days, setDays] = useState(30);
  const [token, setToken] = useState(null);

  // Jeton pré-chargé pour que window.open reste dans le geste utilisateur (sinon bloqué)
  useEffect(() => {
    if (!open) return;
    axios.post(`${API_URL}/api/admin/catalogue/pdf/token`).then(r => setToken(r.data.token)).catch(() => {});
  }, [open]);

  if (!open) return null;

  const download = async (withImages) => {
    const filterQuery = filter === 'recent' ? `&filter=recent&days=${days}` : '';
    const filenameSuffix = filter === 'recent' ? `-nouveautes-${days}j` : '';
    const filename = `catalogue-odelicieuse${withImages ? '-images' : ''}${filenameSuffix}-${new Date().toISOString().slice(0, 10)}.pdf`;
    const useShare = isStandalone() || (!!navigator.canShare && navigator.maxTouchPoints > 1);

    // Version illustrée hors mode app / sans partage natif : ouverture dans un onglet (iOS Safari)
    if (withImages && !useShare) {
      const toastId = toast.loading('Préparation du catalogue illustré…');
      try {
        const tk = token || (await axios.post(`${API_URL}/api/admin/catalogue/pdf/token`)).data.token;
        const directUrl = `${API_URL}/api/admin/catalogue/pdf?with_images=true&token=${encodeURIComponent(tk)}${filterQuery}`;
        const win = window.open(directUrl, '_blank', 'noopener,noreferrer');
        if (!win) window.location.href = directUrl;
        toast.dismiss(toastId);
        toast.success('Catalogue ouvert. Utilisez « Partager » pour l\'envoyer.');
        onClose();
      } catch (err) {
        toast.dismiss(toastId);
        toast.error('Erreur lors de la génération du catalogue');
      }
      return;
    }

    const toastId = toast.loading(withImages ? 'Préparation du catalogue illustré…' : 'Génération du catalogue…');
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/catalogue/pdf?with_images=${withImages}${filterQuery}`,
        { responseType: 'blob', timeout: 60000 }
      );
      toast.dismiss(toastId);
      const result = await deliverPdf(new Blob([response.data], { type: 'application/pdf' }), filename);
      if (result === 'shared') toast.success('Catalogue partagé');
      else if (result === 'opened') toast.success('Catalogue ouvert. Utilisez « Partager » pour l\'envoyer.');
      else if (result === 'downloaded') toast.success('Catalogue téléchargé');
      if (result !== 'cancelled') onClose();
    } catch (err) {
      toast.dismiss(toastId);
      const status = err?.response?.status;
      if (status === 404) {
        toast.error('Aucun produit ne correspond à cette période');
      } else if (withImages && filter === 'all') {
        try {
          await axios.post(`${API_URL}/api/admin/catalogue/pdf/refresh`);
          toast.info('Mise à jour du catalogue en cours (~30 s). Réessayez dans un instant.', { duration: 8000 });
        } catch {
          toast.error('Erreur lors de la génération du catalogue');
        }
      } else {
        toast.error('Erreur lors de la génération du catalogue');
      }
    }
  };

  const scopeBtn = (value, title, hint) => (
    <button
      type="button"
      onClick={() => setFilter(value)}
      className={`p-3 text-left text-xs border transition-colors ${
        filter === value
          ? 'border-[color:var(--color-primary)] bg-[color:rgb(var(--primary-rgb)/0.10)] text-white'
          : 'border-white/10 text-zinc-400 hover:border-white/30'
      }`}
      data-testid={`catalogue-scope-${value}`}
    >
      <p className="font-medium mb-0.5">{title}</p>
      <p className="text-zinc-500 text-[10px]">{hint}</p>
    </button>
  );

  const formatBtn = (onClick, testid, primary, icon, title, hint, speed) => (
    <button type="button" onClick={onClick}
      className={`w-full text-left p-4 border transition-colors group ${
        primary
          ? 'border-[color:rgb(var(--primary-rgb)/0.30)] hover:bg-[color:rgb(var(--primary-rgb)/0.05)]'
          : 'border-white/10 hover:bg-white/5 hover:border-[color:rgb(var(--primary-rgb)/0.30)]'
      }`}
      data-testid={testid}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-white font-medium mb-1 group-hover:text-[color:var(--color-primary)] transition-colors">
            {icon} {title}
          </p>
          <p className="text-zinc-500 text-xs">{hint}</p>
        </div>
        <span className="text-zinc-600 text-xs whitespace-nowrap">{speed}</span>
      </div>
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
      onClick={onClose}
      data-testid="catalogue-modal"
    >
      <div className="bg-[color:var(--color-surface)] border border-white/10 max-w-lg w-full p-8 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[color:rgb(var(--primary-rgb)/0.10)] flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-[color:var(--color-primary)]" />
          </div>
          <div>
            <h3 className="text-white text-xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Télécharger le catalogue
            </h3>
            <p className="text-zinc-500 text-xs">Choisissez la portée et le format</p>
          </div>
        </div>

        {/* Scope */}
        <div className="mb-5">
          <p className="text-[color:var(--color-primary)] text-[10px] uppercase tracking-[0.25em] mb-2">Portée</p>
          <div className="grid grid-cols-2 gap-2">
            {scopeBtn('all', 'Tout le catalogue', 'Tous les produits en stock')}
            {scopeBtn('recent', 'Nouveautés & réassorts', 'Récemment ajoutés ou réapprovisionnés')}
          </div>

          {filter === 'recent' && (
            <div className="mt-3 flex items-center gap-2" data-testid="catalogue-days-selector">
              <span className="text-zinc-500 text-xs">Période :</span>
              {[7, 14, 30, 60].map((d) => (
                <button key={d} type="button" onClick={() => setDays(d)}
                  className={`px-3 py-1 text-xs border transition-colors ${
                    days === d
                      ? 'border-[color:var(--color-primary)] bg-[color:var(--color-primary)] text-black'
                      : 'border-white/10 text-zinc-400 hover:border-white/30'
                  }`}
                  data-testid={`catalogue-days-${d}`}>
                  {d} j
                </button>
              ))}
            </div>
          )}
        </div>

        <p className="text-[color:var(--color-primary)] text-[10px] uppercase tracking-[0.25em] mb-2">Format</p>
        <div className="space-y-3">
          {formatBtn(() => download(false), 'catalogue-fast-btn', true,
            '⚡', 'Version rapide (texte)',
            'Génération instantanée · PDF léger (~30 Ko) · Sans images', '< 1 s')}
          {formatBtn(() => download(true), 'catalogue-images-btn', false,
            '🖼️', 'Version illustrée (avec images)',
            'Idéal pour envoyer par WhatsApp ou email · ~2,5 Mo · S\'ouvre dans un nouvel onglet', '~1 s')}
        </div>

        <div className="mt-6">
          <PublicCatalogueLink />
        </div>

        <button type="button" onClick={onClose}
          className="mt-6 text-zinc-500 hover:text-white text-sm"
          data-testid="catalogue-cancel-btn">
          Annuler
        </button>
      </div>
    </div>
  );
};
